import type { BenefitDetail, EligibilityResult, MedicalSummary, CostSharingResolution, CostSharingGroup } from './pipelineTypes.js'

/** STC priority: most specific to least specific for office-based optometry. */
const STC_PRIORITY = ['98', '96', '30'] as const

/** STCs excluded from Medical tab — not relevant to office-based medical optometry visits. */
const EXCLUDED_STCS = new Set([
  'AL',  // Vision (handled separately)
  'BZ',  // Preventive / Well
  '47',  // Hospital (inpatient)
  '48',  // Hospital - Inpatient
  '50',  // Hospital - Outpatient / Urgent Care
  '51',  // Hospital - Outpatient
  '52',  // Hospital - Emergency
  '86',  // Emergency Services
  'UC',  // Urgent Care
  'MH',  // Mental Health
  '35',  // Dental
  '88',  // Pharmacy
])

// --- Coverage-level helpers ---

function isIndividual(cl: string): boolean {
  const upper = cl.toUpperCase()
  return upper === 'IND' || upper === 'INDIVIDUAL' || upper === 'EMPLOYEE ONLY'
}

function isFamily(cl: string): boolean {
  const upper = cl.trim().toUpperCase()
  return upper === 'FAM' || upper === 'FAMILY' || upper === 'FAMILY AND DEPENDENTS'
}

function isUnknownCoverageLevel(cl: string): boolean {
  return cl.toUpperCase() === 'UNKNOWN'
}

// --- Network-indicator helpers ---

function isInNetwork(ni: string | undefined): boolean {
  if (!ni) return false
  const upper = ni.toUpperCase()
  return upper === 'Y' || upper === 'YES'
}

function isNotApplicable(ni: string | undefined): boolean {
  if (!ni) return false
  const upper = ni.toUpperCase()
  return upper === 'W' || upper === 'NOT APPLICABLE'
}

function isOutOfNetwork(ni: string | undefined): boolean {
  if (!ni) return false
  const upper = ni.toUpperCase()
  return upper === 'N' || upper === 'NO'
}

function isUnknownNetwork(ni: string | undefined): boolean {
  if (!ni) return true
  return ni.toUpperCase() === 'UNKNOWN'
}

/**
 * True when EVERY service type on a row belongs to another benefit domain
 * (vision, dental, pharmacy, ER...), so the row must never be read as medical.
 * Untagged rows are NOT off-domain — an EB03-less row is plan-level generic.
 *
 * Exported because the 271 transformer needs the same judgement to keep an AL
 * (vision) active-coverage row from naming the whole plan (#232).
 */
export function isOffDomainStcs(stcs: readonly string[] | undefined): boolean {
  if (!stcs || stcs.length === 0) return false
  return stcs.every((s: string) => EXCLUDED_STCS.has(s))
}

function isOffDomainRow(b: BenefitDetail): boolean {
  return isOffDomainStcs(b.serviceTypeCodes)
}

function filterByCode(benefits: BenefitDetail[], code: string): BenefitDetail[] {
  return benefits.filter(b => {
    if ((b.code ?? '') !== code) return false
    return !isOffDomainRow(b)
  })
}

function resolveCostSharing(
  rows: BenefitDetail[],
  direction: 'in' | 'oon',
  field: 'amount' | 'percent',
): CostSharingResolution | null {
  for (const stc of STC_PRIORITY) {
    const allStcRows = rows.filter(b => (b.serviceTypeCodes ?? []).includes(stc))
    if (allStcRows.length === 0) continue

    const indRows = allStcRows.filter(b => isIndividual(b.coverageLevel))
    const stcRows = indRows.length > 0
      ? indRows
      : allStcRows.filter(b => isUnknownCoverageLevel(b.coverageLevel))
    if (stcRows.length === 0) continue

    let candidates: BenefitDetail[]
    let networkUnspecified = false

    if (direction === 'in') {
      const yRows = stcRows.filter(b => isInNetwork(b.networkIndicator))
      const wRows = stcRows.filter(b => isNotApplicable(b.networkIndicator))
      const unknownRows = stcRows.filter(b => isUnknownNetwork(b.networkIndicator))
      if (yRows.length > 0) {
        candidates = yRows
      } else if (wRows.length > 0) {
        candidates = wRows
        // W (not applicable) = plan doesn't distinguish in/out of network.
        // Surface this as networkUnspecified so the caller can render the
        // collection amount with a "network not distinguished" badge.
        networkUnspecified = true
      } else {
        candidates = unknownRows
        networkUnspecified = unknownRows.length > 0
      }
    } else {
      const nRows = stcRows.filter(b => isOutOfNetwork(b.networkIndicator))
      const unknownRows = stcRows.filter(b => isUnknownNetwork(b.networkIndicator))
      if (nRows.length > 0) {
        candidates = nRows
      } else {
        candidates = unknownRows
        networkUnspecified = unknownRows.length > 0
      }
    }
    if (candidates.length === 0) continue

    const planLevel = stc === '30'

    const values = new Set<number>()
    for (const b of candidates) {
      const val = field === 'amount' ? b.amount : b.percent
      if (val != null) values.add(val)
    }
    if (values.size === 0) continue

    const labels = new Set<string>()
    for (const b of candidates) {
      if (b.networkLabel) labels.add(b.networkLabel)
    }

    if (labels.size > 1) {
      const tiers: { label: string; value: number }[] = []
      for (const label of labels) {
        const tierRows = candidates.filter(b => b.networkLabel === label)
        const tierValues = new Set<number>()
        for (const b of tierRows) {
          const val = field === 'amount' ? b.amount : b.percent
          if (val != null) tierValues.add(val)
        }
        if (tierValues.size > 0) {
          tiers.push({ label, value: Math.min(...tierValues) })
        }
      }
      tiers.sort((a, b) => a.value - b.value)

      const distinctTierValues = new Set(tiers.map(t => t.value))
      if (distinctTierValues.size === 1) {
        return { mode: 'single', value: tiers[0].value, planLevel, stc, collapsedTierCount: tiers.length, networkUnspecified }
      }

      return { mode: 'tiers', tiers, planLevel, stc, networkUnspecified }
    }

    if (values.size > 1) {
      const sorted = [...values].sort((a, b) => a - b)
      return { mode: 'range', min: sorted[0], max: sorted[sorted.length - 1], planLevel, stc, networkUnspecified }
    }

    return { mode: 'single', value: [...values][0], planLevel, stc, networkUnspecified }
  }

  return null
}

/** The generic "Health Benefit Plan Coverage" bucket — semantically what an
 *  EB03-less (untagged) row is, so untagged rows answer this lookup and no
 *  service-specific one. */
const PLAN_LEVEL_STC = '30'

export function extractPlanLevelAmount(
  benefits: BenefitDetail[],
  code: string,
  direction?: 'in' | 'oon',
  coverageLevel: 'individual' | 'family' = 'individual',
  stc: string = PLAN_LEVEL_STC,
): { total: number | null; remaining: number | null } {
  const ofCode = benefits.filter(b => (b.code ?? '') === code)
  const tagged = ofCode.filter(b => (b.serviceTypeCodes ?? []).includes(stc))
  // Rows the payer never scoped with an EB03. Stedi surfaces the code when the
  // payer sends it, so these are "plan-level, unspecified" — they may only
  // stand in for the generic lookup. Letting them answer a service-specific
  // one is how a vision (AL) deductible arriving untagged used to overwrite
  // the medical deductible, and how a medical row used to render on the
  // Vision tab (#232 review).
  const untagged = stc === PLAN_LEVEL_STC
    ? ofCode.filter(b => (b.serviceTypeCodes ?? []).length === 0)
    : []

  // Tagged rows win outright; untagged ones only fill the fields the tagged
  // tier left empty (a payer that splits total/remaining across a tagged and
  // an untagged row still reads correctly).
  const fromTagged = pickAmounts(tagged, direction, coverageLevel)
  if (untagged.length === 0) return fromTagged
  if (fromTagged.total !== null && fromTagged.remaining !== null) return fromTagged
  const fromUntagged = pickAmounts(untagged, direction, coverageLevel)
  return {
    total: fromTagged.total ?? fromUntagged.total,
    remaining: fromTagged.remaining ?? fromUntagged.remaining,
  }
}

function pickAmounts(
  codeRows: BenefitDetail[],
  direction: 'in' | 'oon' | undefined,
  coverageLevel: 'individual' | 'family',
): { total: number | null; remaining: number | null } {
  const matchFn = coverageLevel === 'family' ? isFamily : isIndividual
  const matchedRows = codeRows.filter(b => matchFn(b.coverageLevel))
  const coverageRows = matchedRows.length > 0
    ? matchedRows
    : codeRows.filter(b => isUnknownCoverageLevel(b.coverageLevel))

  let rows: BenefitDetail[]
  if (direction === 'in') {
    const explicit = coverageRows.filter(b => isInNetwork(b.networkIndicator) || isNotApplicable(b.networkIndicator))
    rows = explicit.length > 0 ? explicit : coverageRows.filter(b => isUnknownNetwork(b.networkIndicator))
  } else if (direction === 'oon') {
    const explicit = coverageRows.filter(b => isOutOfNetwork(b.networkIndicator))
    rows = explicit.length > 0 ? explicit : coverageRows.filter(b => isUnknownNetwork(b.networkIndicator))
  } else {
    rows = coverageRows
  }

  let total: number | null = null
  let totalFromCalendarYear = false
  let remaining: number | null = null

  // Two rules, deliberately separate (#256 review):
  //
  // 1. QUALIFIER-CLASS priority (unchanged from the original code): an
  //    explicit Calendar Year row (tq 23) always outranks a row with no/other
  //    qualifier (Lifetime, Service Year, untyped...) regardless of array
  //    order — an untyped or lifetime amount must never mask the payer's
  //    stated annual total.
  // 2. WITHIN a class, first match wins, not last. When several rows of the
  //    same class qualify inside the same coverage/network tier none of them
  //    is authoritative — the variant list below the card enumerates them
  //    all; the headline is one arbitrary member of that set either way.
  //    First-wins follows the payer's own response order and keeps the number
  //    stable when a later 271 for the same patient APPENDS rows (last-wins
  //    flips it on every append).
  for (const r of rows) {
    const tq = (r.timeQualifier ?? '').toUpperCase()
    if (tq === '23' || tq === 'CALENDAR YEAR') {
      if (r.amount != null && !totalFromCalendarYear) {
        total = r.amount
        totalFromCalendarYear = true
      }
    } else if (tq === '29' || tq === 'REMAINING') {
      if (r.amount != null && remaining === null) remaining = r.amount
    } else if (r.amount != null && total === null) {
      total = r.amount
    }
  }

  return { total, remaining }
}

export function extractMedicalSummary(result: EligibilityResult): MedicalSummary {
  const allBenefits: BenefitDetail[] = result.normalizedBenefits ?? []

  const costSharingBenefits: BenefitDetail[] = (result.normalizedBenefits ?? [])
    .filter(b => b.coverageStatus !== 'not-covered')

  const copayRows = filterByCode(costSharingBenefits, 'B')
  const coinsuranceRows = filterByCode(costSharingBenefits, 'A')

  const copayIn = resolveCostSharing(copayRows, 'in', 'amount')
  const copayOon = resolveCostSharing(copayRows, 'oon', 'amount')
  const coinsuranceIn = resolveCostSharing(coinsuranceRows, 'in', 'percent')
  const coinsuranceOon = resolveCostSharing(coinsuranceRows, 'oon', 'percent')

  const copay: CostSharingGroup | null = (copayIn || copayOon)
    ? { type: 'copay', inNetwork: copayIn, outOfNetwork: copayOon }
    : null

  const coinsurance: CostSharingGroup | null = (coinsuranceIn || coinsuranceOon)
    ? { type: 'coinsurance', inNetwork: coinsuranceIn, outOfNetwork: coinsuranceOon }
    : null

  const hasTiers = [copayIn, copayOon, coinsuranceIn, coinsuranceOon]
    .some(r => r?.mode === 'tiers')

  const missingCostSharing = !copay && !coinsurance

  const deductibleIn = extractPlanLevelAmount(allBenefits, 'C', 'in')
  const oopIn = extractPlanLevelAmount(allBenefits, 'G', 'in')
  const deductibleOon = extractPlanLevelAmount(allBenefits, 'C', 'oon')
  const oopOon = extractPlanLevelAmount(allBenefits, 'G', 'oon')

  return {
    copay,
    coinsurance,
    missingCostSharing,
    hasTiers,
    deductibleTotal: deductibleIn.total,
    deductibleRemaining: deductibleIn.remaining,
    deductibleOonTotal: deductibleOon.total,
    deductibleOonRemaining: deductibleOon.remaining,
    oopTotal: oopIn.total,
    oopRemaining: oopIn.remaining,
    oopOonTotal: oopOon.total,
    oopOonRemaining: oopOon.remaining,
  }
}

export function deriveDisplayStatus(result: EligibilityResult): 'active' | 'inactive' | 'review' | 'lookup_error' {
  if (result.status === 'lookup_error') return 'lookup_error'
  if (result.status !== 'active') {
    return result.status === 'inactive' ? 'inactive' : 'review'
  }

  const benefits: BenefitDetail[] = result.normalizedBenefits ?? []
  // Only MEDICAL cost-sharing confirms the badge. An off-domain row — since
  // #232 that means a vision (AL) exam copay on nearly every 271 — must not
  // flip the badge to green on a plan that shared no medical cost-sharing at
  // all; extractMedicalSummary's `missingCostSharing` (same guard, via
  // filterByCode) and the slideout's "no cost-sharing" notice would then be
  // asserting the opposite of the badge.
  const hasCostDetails = benefits.some(b => {
    if (isOffDomainRow(b)) return false
    const code = b.code ?? ''
    return (code === 'B' && b.amount !== undefined) || (code === 'A' && b.percent !== undefined)
  })

  return hasCostDetails ? 'active' : 'review'
}
